<template>
  <div data-testid="financialclose-project-users" class="cycle-settings-team" :class="{ 'cycle-settings-team--loading': loading }">
    <div v-if="loading" data-testid="financialclose-project-users__loading">
      <v-skeleton-loader type="table-heading, list-item-two-line, article, article" data-testid="financialclose-project-users__loading-skeleton"></v-skeleton-loader>
    </div>
    <template v-else>
      <div class="cycle-settings-team__heading" data-testid="cycle-settings-team__heading">
        <p class="cycle-settings-team__description noTransform" data-testid="cycle-settings-team__description">
          Manage team members participating in this cycle.
        </p>
        <div class="cycle-settings-team__actions" data-testid="cycle-settings-team__actions">
          <v-btn
            v-if="hasUserFilterActive"
            text
            class="cycle-settings-team__clear-filter"
            data-testid="financialclose-project-users__team-clear-filter-btn"
            @click="clearUserTableFilters"
          >
            <span>Clear Filter</span>
          </v-btn>
          <v-btn
            v-if="canManageTeam"
            class="cycle-settings-team__add-btn"
            data-testid="financialclose-project-users__adduser-btn"
            @click="openAddTeamMemberModal()"
          >
            <img
              class="cycle-settings-team__add-icon"
              src="/img/cycle-settings/add-icon.svg"
              alt=""
              data-testid="financialclose-project-users__adduser-icon"
            />
            <span data-testid="financialclose-project-users__adduser-label">Add member</span>
          </v-btn>
          <div v-if="canManageTeam" class="cycle-settings-team__divider" aria-hidden="true"></div>
          <div class="cycle-settings-team__activity" data-testid="financialclose-project-users__activitylog-wrap">
            <activity-log
              :resource-id="metadata._id"
              data-testid="financialclose-project-users__activitylog"
              row-description="User Management"
              :type="$options.ACTIVITY_LOG_TYPES.USERS"
            />
          </div>
        </div>
      </div>

      <cycle-team-members-table
        ref="teamMembersTable"
        :items="formattedUsers"
        :source-items="formattedUsersSource"
        :headers="userTableHeaders"
        :selectfilter="userFilterCriteria"
        :items-per-page="itemsPerPage"
        :show-toolbar="false"
        data-testid-prefix="cycle-settings-team"
        @filter-item="applyUserFilters"
        @clear-item="clearUserFilters"
        @filter-active-change="hasUserFilterActive = $event"
      >
        <template #actions="{ item }">
          <button
            v-if="isSelfScopedTeam"
            type="button"
            class="cycle-settings-team__role"
            disabled
            data-testid="cycle-settings-team__role-btn"
          >
            <span>{{ roleLabel(item) }}</span>
          </button>
          <div v-else class="cycle-settings-team__action-group">
            <v-menu
              offset-y
              :disabled="isRoleControlDisabled(item)"
              content-class="cycle-settings-team__role-menu"
            >
              <template v-slot:activator="{ on, attrs }">
                <button
                  type="button"
                  class="cycle-settings-team__role"
                  v-bind="attrs"
                  v-on="on"
                  :disabled="isRoleControlDisabled(item)"
                  data-testid="cycle-settings-team__role-btn"
                >
                  <span>{{ roleLabel(item) }}</span>
                  <v-icon class="cycle-settings-team__role-chevron" size="16">mdi-chevron-down</v-icon>
                </button>
              </template>
              <v-list dense>
                <v-list-item
                  v-for="role in roleOptions"
                  :key="role.value"
                  :disabled="item.type === role.value"
                  @click="changeMemberRole(item, role.value)"
                  :data-testid="`cycle-settings-team__role-option--${role.value}`"
                >
                  <v-list-item-title>{{ role.label }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <v-tooltip bottom content-class="tooltip-bottom">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-if="canManageTeam"
                  icon
                  class="cycle-settings-team__icon-btn"
                  v-bind="attrs"
                  v-on="on"
                  data-testid="financialclose-project-users__action-replace-btn"
                  @click="replaceUserDialog(item._id, item.type)"
                >
                  <img
                    class="cycle-settings-team__icon"
                    src="/img/cycle-settings/user-switch.svg"
                    alt="Reassign"
                    data-testid="financialclose-project-users__action-replace-icon"
                  />
                </v-btn>
              </template>
              <span>Replace User</span>
            </v-tooltip>

            <v-tooltip bottom content-class="tooltip-bottom">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-if="canManageTeam && item._id !== userid"
                  icon
                  class="cycle-settings-team__icon-btn"
                  v-bind="attrs"
                  v-on="on"
                  data-testid="financialclose-project-users__action-remove-btn"
                  @click="deleteUserDialogBox(item)"
                >
                  <img
                    class="cycle-settings-team__icon"
                    src="/img/cycle-settings/delete.svg"
                    alt="Remove"
                    data-testid="financialclose-project-users__action-remove-icon"
                  />
                </v-btn>
              </template>
              <span>Remove User</span>
            </v-tooltip>
          </div>
        </template>
      </cycle-team-members-table>
    </template>

    <add-team-member-modal
      :visible.sync="addTeamMemberVisible"
      :eligible-users="eligibleTeamMembers"
      :loading="addTeamMemberLoading"
      @submit="onAddTeamMembers"
      @close="onAddTeamMemberClose"
    />

    <change-info-owner-drawer
      :visible.sync="infoownerdialog"
      :loading="infoownerloading"
      :change-type.sync="infoownerchangetype"
      :form.sync="infoownerdata"
      :possible-owners="possibleinfoowners"
      @submit="onReplaceInfoOwner"
    />

    <replace-team-member-drawer
      :visible.sync="replaceuserdialog"
      :loading="replaceuserloading"
      :from-user-name="(userdata[replaceuserid] || {}).displayname || 'Unknown'"
      :to-user-id.sync="replaceuserdata.userid"
      :replace-user-list="replaceuserlist"
      :show-task-warning="Boolean((userchecklistcount[replaceuserid] || {}).preparer || (userchecklistcount[replaceuserid] || {}).approver)"
      @submit="replaceUser"
    />

    <remove-team-member-drawer
      :visible.sync="deleteuserdialog"
      :loading="deleteuserloading"
      :user-name="(deleteuserdata || {}).name || 'Unknown'"
      :show-task-warning="Boolean((userchecklistcount[deleteuserdata._id] || {}).preparer || (userchecklistcount[deleteuserdata._id] || {}).approver)"
      @submit="onRemoveUserConfirm"
    />
  </div>
</template>
<script>
import { mapActions } from 'vuex';
import { MESSAGES, ACTIVITY_LOG_TYPES, MANAGE_USER_OP } from '../../utils/constants';
import ActivityLog from '../../components/ActivityLog/ActivityLog.vue';
import AddTeamMemberModal from './CycleSettings/components/AddTeamMemberModal.vue';
import CycleTeamMembersTable from './CycleSettings/components/CycleTeamMembersTable.vue';
import ChangeInfoOwnerDrawer from './CycleSettings/components/ChangeInfoOwnerDrawer.vue';
import ReplaceTeamMemberDrawer from './CycleSettings/components/ReplaceTeamMemberDrawer.vue';
import RemoveTeamMemberDrawer from './CycleSettings/components/RemoveTeamMemberDrawer.vue';
import { NONE_SENTINEL } from '@/views/common/DataTableAction/constants';
    export default {
        name: 'financialclose_project_users',
        // Expose constant for template interpolation via $options
        ACTIVITY_LOG_TYPES,
        // props: ['props'],
        data: function(){
          return {
            right:"financialclose_project",
            metadata: {},
            checklist: [],
            pbcdata: {},
            userchecklistcount: {},
            userid: "",
            formdata: {},
            userdata: {},
            stage: -1,      
            id: "",
            loading: false,
      
            addTeamMemberVisible: false,
            addTeamMemberLoading: false,
            associateuserdialog: false,
            associateuserloading: false,
            associateuserdata: {},
            associateusererror: {},
            associateuserlist: [],
            infoownerchangetype: "",
            oldinfoownerdata: {},
            infoownerdialog: false,
            infoownerloading: false,
            infoownerdata: {},
            allinfoowners: [],
            userlist: [],
            managerlist: [],
            infoowners: [],
            possibleinfoowners: [],
            replaceuserdialog: false,
            replaceuserloading: false,
            replaceuserid: "",
            replaceuserdata: {},
            replaceuserlist: [],
            deleteuserdialog: false,
            deleteuserloading: false,
            deleteuserdata: {},
            usertypes: [
              { name: "General", value: "general" },
              { name: "Manager", value: "manager" }
            ],
            userTableHeaders: [
              {
                value: "name",
                text: "Member",
                sortable: true,
                filterable: true,
                visible: true,
                headerClass: "cycle-team-members-table__col--member",
                cellClass: "cycle-team-members-table__col--member",
              },
              {
                value: "action",
                text: "Actions",
                sortable: false,
                filterable: false,
                visible: true,
                headerClass: "cycle-team-members-table__col--actions-wide",
                cellClass: "cycle-team-members-table__col--actions-wide",
              }
            ],
            infoOwnerTableHeaders: [
              {
                value: "name",
                text: "Member",
                sortable: true,
                filterable: true,
                visible: true,
                headerClass: "cycle-team-members-table__col--member",
                cellClass: "cycle-team-members-table__col--member",
              },
              {
                value: "information",
                text: "Information Requests",
                sortable: false,
                filterable: false,
                visible: true,
                headerClass: "cycle-team-members-table__col--info-requests",
                cellClass: "cycle-team-members-table__col--info-requests",
              },
              {
                value: "progress",
                text: "Progress",
                sortable: false,
                filterable: false,
                visible: true,
                headerClass: "cycle-team-members-table__col--progress",
                cellClass: "cycle-team-members-table__col--progress",
              },
              {
                value: "action",
                text: "Actions",
                sortable: false,
                filterable: false,
                visible: true,
                headerClass: "cycle-team-members-table__col--actions",
                cellClass: "cycle-team-members-table__col--actions",
              }
            ],
            formattedUsers: [],
            formattedUsersSource: [],
            userFilterCriteria: {},
            hasUserFilterActive: false,
            /** NXTGEN-7362 §7.8 — from getuserdetails when present */
            teamVisibility: null,
            formattedInfoOwners: [],
            formattedInfoOwnersSource: [],
            infoOwnerFilterCriteria: {},
            hasInfoOwnerFilterActive: false,
            headersPushed: false,
            activitylogconfig: {
              userassociate: {
                icon: "mdi-plus",
                color: "info",
                displaytext: "User Associated",
              },
              userreplaced: {
                icon: "mdi-account-convert",
                color: "info",
                displaytext: "User Replaced",
              },
              userremoved: {
                icon: "mdi-close",
                color: "error",
                displaytext: "User Removed",
              },
              usermoved: { icon: "mdi-sync", color: "", displaytext: "Updated" },
            },
            itemsPerPage:10,
            projectUsersWithChecklistData: [],
            projectUserIds: [],
            userDetails:{ activeUserIds: [] }
          }
        },
        components: {
         ActivityLog,
         AddTeamMemberModal,
         CycleTeamMembersTable,
         ChangeInfoOwnerDrawer,
         ReplaceTeamMemberDrawer,
         RemoveTeamMemberDrawer
        },
        created() {
          this.refreshData();
        },
        activated() {
          this.refreshData();
        },
        methods: {
          ...mapActions('project', [
            'fetchCycleUserDetails',
            'fetchUsersFormData',
            'manageCycleUserOperation',
            'replaceInfoOwner'
          ]),
          getItemData(item, key) {
            return item[key];
          },
          getMemberInitials(name) {
            const clean = this.displayMemberName(name);
            const parts = clean.split(/\s+/).filter(Boolean);
            if (parts.length >= 2) {
              return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
            }
            return (clean.slice(0, 2) || '?').toUpperCase();
          },
          displayMemberName(name) {
            return String(name || '').replace(/\s*\(Inactive\)\s*/gi, '').trim();
          },
          isInactiveName(name) {
            return String(name || '').includes('(Inactive)');
          },
          roleLabel(item) {
            const type = item && item.type;
            if (
              type !== 'manager'
              && item
              && String(item._id) === String(this.userid)
              && this.teamVisibility?.cycleRole === 'manager'
            ) {
              return 'Manager';
            }
            if (type === 'manager') return 'Manager';
            if (type === 'general') return 'General';
            return 'Unknown';
          },
          isRoleControlDisabled(item) {
            if (!item || String(item._id) === String(this.userid)) return true;
            return !this.canManageTeam;
          },
          infoOwnerProgressPercent(item) {
            if (!item) return 0;
            const total =
              (item.requested || 0) +
              (item.pending || 0) +
              (item.approved || 0) +
              (item.cancelled || 0);
            if (!total) return 0;
            return Number((((item.approved || 0) / total) * 100).toFixed(0));
          },
          changeMemberRole(item, nextType) {
            if (this.isRoleControlDisabled(item)) return;
            if (!item || !nextType || item.type === nextType) return;
            this.manageUser(MANAGE_USER_OP.MOVE, item.type, item._id);
          },
          formatUserData() {
            let formattedUser = {};
            let formattedUsers = [];
            const source = this.projectUsersWithChecklistData || [];
            const scoped = this.teamVisibility?.scope === 'self'
              ? source.filter((item) => String(item._id) === String(this.userid))
              : source;
            scoped.forEach((item, index) => {
              const isActor = String(item._id) === String(this.userid);
              const type = isActor && this.teamVisibility?.cycleRole === 'manager'
                ? 'manager'
                : item.type;
              formattedUser = {
                __key: index,
                __formatedkey: index,
                _id: item._id,
                indexno: index + 1,
                name: this.userDetails?.activeUserIds?.includes(item._id) ? item.name : (item.name || 'Unknown') + ' (Inactive)',
                email: item.email,
                color: item.color,
                type,
                tasklist: item.tasklist,
                progress: item.progress,
              };
              formattedUsers.push(formattedUser);
            });
            this.formattedUsers = formattedUsers;
            this.formattedUsersSource = formattedUsers.slice();
          },
          formatInfoOwnerData(){
            let formattedInfoOwner = {};
            let formattedInfoOwners = [];
            this.infoowners.forEach((item, index) => {
              formattedInfoOwner = {
                __key: index,
                __formatedkey: index,
                _id: item._id,
                indexno: index + 1,
                name: item.info_owner_detail?.name || '',
                email: item.info_owner_detail?.email || '',
                approved:item.approved,
                cancelled:item.cancelled,
                pending:item.pending,
                requested:item.requested,
                info_owner_detail:item.info_owner_detail,
                info_owner:item.info_owner,
              };
              formattedInfoOwners.push(formattedInfoOwner);
            });
            
            this.formattedInfoOwners = formattedInfoOwners;
            this.formattedInfoOwnersSource = formattedInfoOwners.slice();
          },
          refreshData() {
            this.id = this.$route.params.id || "";
            this.userid = this.$store.state.user.id;
            this.getAllActiveUsers();
            this.getData();
          },
          async getData() {
            this.loading = true;
            try {
              const details = await this.fetchCycleUserDetails(this.id);
              const data = details || {};
              this.metadata = data.metadata || {};
              this.userlist = this.metadata.userlist || [];
              this.managerlist = this.metadata.managerlist || [];
              this.stage = this.metadata.stage;
              this.userchecklistcount = data.userchecklistcount || {};
              this.infoowners = data.infoowners || [];
              // NXTGEN-7362 §7.8 — present when server ships teamVisibility; else keep today's behavior
              this.teamVisibility = data.teamVisibility || null;
              this.projectUserIds = this.userlist.concat(this.managerlist);
              this.formatInfoOwnerData();

              const formdata = await this.fetchUsersFormData();
              this.formdata = formdata || {};
              this.allinfoowners = this.formdata.infoowners || [];
              this.userdata = {};
              const matchedUsers = (this.formdata.users || []).filter((user) =>
                this.projectUserIds.includes(user._id)
              );
              const colors = this.$nova.getColors((this.formdata.users || []).length, 0.2);
              for (let i = 0; i < (this.formdata.users || []).length; i++) {
                const el = (this.formdata.users || [])[i];
                this.userdata[el._id] = el;
                this.userdata[el._id].color = colors[i];
              }
              this.projectUsersWithChecklistData = this.matchUsersWithData(
                matchedUsers,
                this.userchecklistcount,
                this.managerlist,
                this.userlist
              );
              this.formatUserData();
            } catch (err) {
              console.error('[financialclose_project_users]: getData error', err);
            } finally {
              this.loading = false;
            }
          },
        async getAllActiveUsers(){
            try{
              await this.$store.dispatch("tasks/getAllUsersWithStatus",{isActiveUsersRequired:true})
              this.$set(this.userDetails, 'activeUserIds', this.$store.getters["tasks/getActiveUsersIds"] || []);
            }catch(error){
              console.error("Error fetching users:", error);
              this.$store.commit("sbError", MESSAGES.FAILED_TO_GET_USERS);
              this.$set(this.userDetails, 'activeUserIds', []);
            }
          },
          deleteUserDialogBox(data) {
            this.deleteuserdata = {...data};
            this.deleteuserdialog = true;
          },
          openAddTeamMemberModal() {
            this.addTeamMemberVisible = true;
          },
          onAddTeamMemberClose() {
            if (!this.addTeamMemberLoading) {
              this.addTeamMemberVisible = false;
            }
          },
          async onAddTeamMembers({ userIds, role }) {
            if (!userIds || !userIds.length) {
              this.$store.commit('sbError', MESSAGES.TEAM_SELECT_MEMBERS);
              return;
            }
            const cycleId = this.metadata._id || this.id;
            this.addTeamMemberLoading = true;
            try {
              await this.$store.dispatch('project/manageCycleUsers', {
                cycleId,
                userIds,
                role
              });
              await this.refreshData();
              this.addTeamMemberVisible = false;
            } catch (err) {
              console.log('[financialclose_project_users]:', err);
            } finally {
              this.addTeamMemberLoading = false;
            }
          },
          associateUserDialogBox() {
            this.openAddTeamMemberModal();
          },
          replaceInfoOwnerDialog(d) {
            this.infoownerdata = {};
            this.oldinfoownerdata = d;
            this.possibleinfoowners = [];
            for (const i of this.allinfoowners) {
              if(i._id !== d._id && i.status) {
                const ownerName = i.name || '';
                const ownerEmail = i.email || '';
                this.possibleinfoowners.push({
                  ...i,
                  displayName: ownerEmail ? `${ownerName} (${ownerEmail})` : ownerName,
                });
              }
            }
            this.infoownerchangetype = "existing";
            this.infoownerdialog = true;
          },
          async onReplaceInfoOwner() {
            this.infoownerdata.oldownerid = this.oldinfoownerdata._id;
            this.infoownerdata.type = this.infoownerchangetype;
            this.infoownerdata.cycleid = this.metadata._id;
            this.infoownerloading = true;
            try {
              await this.replaceInfoOwner({
                pbcProjectId: this.metadata.pbcproject,
                data: this.infoownerdata
              });
              await this.getData();
            } catch (err) {
              console.error('[financialclose_project_users]: replaceInfoOwner error', err);
            } finally {
              this.infoownerloading = false;
              this.infoownerdialog = false;
            }
          },
          validateAssociateUserData(data) {
          const errors = {};
          // User ID validation
          if (
            !data.userid ||
            typeof data.userid !== 'string' ||
            data.userid.trim().length < 2
          ) {
            errors.userid = 'User is required.';
          }
          // Type validation
          if (
            !data.type ||
            typeof data.type !== 'string' ||
            data.type.trim().length < 2
          ) {
            errors.type = 'Type is required';
          }
          return errors;
        },
      
          async manageUser(type, usertype = 'general', uid = '', fromuserid = '') {
            if (type === MANAGE_USER_OP.MOVE) {
              if (!this.userDetails?.activeUserIds?.includes(uid)) {
                this.$store.commit('sbError', MESSAGES.SELECTED_INACTIVE_USERS);
                return;
              }
              this.loading = true;
            } else if (type === MANAGE_USER_OP.REMOVE) {
              this.deleteuserloading = true;
            } else if (type === MANAGE_USER_OP.REPLACE) {
              this.replaceuserloading = true;
            }

            try {
              await this.manageCycleUserOperation({
                cycleId: this.metadata._id,
                type,
                userid: type === MANAGE_USER_OP.REPLACE ? undefined : uid,
                usertype,
                fromuserid: type === MANAGE_USER_OP.REPLACE ? fromuserid : undefined,
                touserid: type === MANAGE_USER_OP.REPLACE ? uid : undefined
              });
              this.deleteuserdialog = false;
              this.replaceuserdialog = false;
              await this.refreshData();
            } catch (err) {
              console.error('[financialclose_project_users]: manageUser error', err);
              if (type === MANAGE_USER_OP.REPLACE) {
                this.replaceuserdialog = false;
              }
            } finally {
              this.loading = false;
              this.deleteuserloading = false;
              this.replaceuserloading = false;
            }
          },

          onRemoveUserConfirm() {
            this.manageUser(MANAGE_USER_OP.REMOVE, this.deleteuserdata.type, this.deleteuserdata._id);
          },
      
          replaceUserDialog(uid, type) {
            this.replaceuserdata = {type: type};
            this.replaceuserlist = [];
            this.replaceuserid = uid;
            for (const k in this.formdata.users) {
              if (Object.hasOwnProperty.call(this.formdata.users, k)) {
                const el = this.formdata.users[k];
                if(el._id !== uid && this.userDetails?.activeUserIds?.includes(el._id)) this.replaceuserlist.push(el);
              }
            }
            this.replaceuserdialog = true;
          },
           replaceUser() {
            this.manageUser(
              MANAGE_USER_OP.REPLACE,
              this.replaceuserdata.type,
              this.replaceuserdata.userid,
              this.replaceuserid
            );
          },
          _cleanFilterCriteria(item) {
            return Object.entries(item || {}).reduce((acc, [key, values]) => {
              if (Array.isArray(values)) {
                const real = values.filter(v => v !== NONE_SENTINEL);
                if (real.length > 0) {
                  acc[key] = real;
                } else if (values.includes(NONE_SENTINEL)) {
                  // Select-all unchecked emits only the sentinel. Keep it so the
                  // column matches no rows instead of falling back to every user.
                  acc[key] = [NONE_SENTINEL];
                }
              } else if (values !== undefined && values !== null) {
                acc[key] = values;
              }
              return acc;
            }, {});
          },
          applyUserFilters(item) {
            this.userFilterCriteria = item;
            this.formattedUsers = this.$nova.filterByDynamicCriteria(this.formattedUsersSource, this._cleanFilterCriteria(item));
          },
          clearUserFilters() {
            this.userFilterCriteria = {};
            this.formattedUsers = this.formattedUsersSource.slice();
            this.hasUserFilterActive = false;
          },
          clearUserTableFilters() {
            this.$refs.teamMembersTable?.clearFilters?.();
          },
          applyInfoOwnerFilters(item) {
            this.infoOwnerFilterCriteria = item;
            this.formattedInfoOwners = this.$nova.filterByDynamicCriteria(this.formattedInfoOwnersSource, this._cleanFilterCriteria(item));
          },
          clearInfoOwnerFilters() {
            this.infoOwnerFilterCriteria = {};
            this.formattedInfoOwners = this.formattedInfoOwnersSource.slice();
            this.hasInfoOwnerFilterActive = false;
          },
          clearInfoOwnerTableFilters() {
            this.$refs.infoOwnersTable?.clearFilters?.();
          },
        matchUsersWithData(users, dataObject, managerList, userList) {
          
        return Object.entries(dataObject).map(([id]) => {
          const user = users.find((u) => u._id === id);
          if (user) {
            const type = managerList.includes(id) ? "manager" : userList.includes(id) ? "general" : "unknown";
            return {
              name: user.name,
              email: user.email,
              tasklist: this.userchecklistcount[user._id],
              progress: this.userchecklistcount[user._id],
              type: type,
              _id:user._id
            };
          }
          return null;
        }).filter(item => item !== null);
      }
    
         
        },
        computed: {
          canManageTeam() {
            if (this.teamVisibility && typeof this.teamVisibility.canManage === 'boolean') {
              return !!this.teamVisibility.canManage;
            }
            const hasL1 =
              this.$nova.hasRight(this.right, 'edit') || this.$nova.hasRight(this.right, 'add');
            return this.managerlist.indexOf(this.userid) > -1 && hasL1;
          },
          isSelfScopedTeam() {
            return this.teamVisibility?.scope === 'self';
          },
          eligibleTeamMembers() {
            const users = Array.isArray(this.formdata?.users) ? this.formdata.users : [];
            const activeIds = this.userDetails?.activeUserIds || [];
            const seen = new Set();
            return users.filter((user) => {
              if (!user?._id) return false;
              const id = String(user._id);
              if (seen.has(id)) return false;
              if (this.managerlist.indexOf(user._id) > -1) return false;
              if (this.userlist.indexOf(user._id) > -1) return false;
              if (!activeIds.includes(user._id)) return false;
              seen.add(id);
              return true;
            });
          },
          roleOptions() {
            return [
              { label: 'Manager', value: 'manager' },
              { label: 'General', value: 'general' }
            ];
          },
          finaluserlist(){
            let nl = [];
            for (const i of this.managerlist) {
              let ud = {...this.userdata[i]};
              ud.type = "manager";
              nl.push(ud);
            }
            for (const i of this.userlist) {
              let ud = {...this.userdata[i]};
              ud.type = "general";
              nl.push(ud);
            }
            return nl;
          }
        },
        watch: {
          "infoownerchangetype": function(d){
            this.infoownerdata.type = d;
          },
          formattedUsersSource(newList) {
            const cleaned = this._cleanFilterCriteria(this.userFilterCriteria);
            if (Object.keys(cleaned).length > 0) {
              this.formattedUsers = this.$nova.filterByDynamicCriteria(newList, cleaned);
            } else {
              this.formattedUsers = newList.slice();
            }
          },
          formattedInfoOwnersSource(newList) {
            const cleaned = this._cleanFilterCriteria(this.infoOwnerFilterCriteria);
            if (Object.keys(cleaned).length > 0) {
              this.formattedInfoOwners = this.$nova.filterByDynamicCriteria(newList, cleaned);
            } else {
              this.formattedInfoOwners = newList.slice();
            }
          }
        }
      }
</script>

<style lang="scss" scoped>
@import './CycleSettings/CycleSettingsTeam.scss';
</style>
